import React from "react";
import { useParams } from "react-router-dom";
import SeoDetailLayout from "@/components/public-seo/SeoDetailLayout";
import RegulationLongformGuide from "@/components/public-seo/RegulationLongformGuide";
import { regulations } from "@/lib/publicSeoDetails";
import { deslugify } from "@/lib/slugify";
import { getLocalizedRegulationDetail } from "@/lib/regulationLongformContent";

const backLabels = {
  en: "Back to Regulations",
  he: "חזרה לתקנות",
  es: "Volver a regulaciones",
  it: "Torna alle regole",
  fr: "Retour aux règles",
  de: "Zurück zu Regeln",
  pt: "Voltar aos regulamentos",
  ar: "العودة إلى اللوائح",
  el: "Πίσω στους κανονισμούς",
  ru: "Назад к правилам",
  tr: "Kurallara dön",
  zh: "返回法规"
};

const metaLabels = {
  en: { reviewed: "Last reviewed", effective: "Effective from", responsible: "Responsible authority", changed: "What changed" }, he: { reviewed: "נבדק לאחרונה", effective: "בתוקף מ", responsible: "רשות אחראית", changed: "מה השתנה" }, es: { reviewed: "Última revisión", effective: "Vigente desde", responsible: "Autoridad responsable", changed: "Qué cambió" }, it: { reviewed: "Ultima revisione", effective: "In vigore dal", responsible: "Autorità responsabile", changed: "Cosa è cambiato" }, fr: { reviewed: "Dernière révision", effective: "En vigueur depuis", responsible: "Autorité responsable", changed: "Ce qui a changé" }, de: { reviewed: "Zuletzt geprüft", effective: "Gültig ab", responsible: "Zuständige Behörde", changed: "Was geändert wurde" }, pt: { reviewed: "Última revisão", effective: "Em vigor desde", responsible: "Autoridade responsável", changed: "O que mudou" }, ar: { reviewed: "آخر مراجعة", effective: "ساري من", responsible: "الجهة المسؤولة", changed: "ما الذي تغير" }, el: { reviewed: "Τελευταίος έλεγχος", effective: "Ισχύει από", responsible: "Υπεύθυνη αρχή", changed: "Τι άλλαξε" }, ru: { reviewed: "Последняя проверка", effective: "Действует с", responsible: "Ответственный орган", changed: "Что изменилось" }, tr: { reviewed: "Son inceleme", effective: "Yürürlük başlangıcı", responsible: "Sorumlu otorite", changed: "Ne değişti" }, zh: { reviewed: "最后审核", effective: "生效日期", responsible: "负责机构", changed: "更新内容" }
};

const detailStartDates = { colregs: "1977-07-15", "navigation-lights": "1977-07-15", "waste-discharge": "1983-10-02", "vhf-radio": "1999-02-01", "epirb-distress-beacons": "1999-02-01", "ais-requirements": "2004-12-31", "safety-equipment": "1974-05-25" };

const authorityLabels = {
  en: "National maritime authority / port authority", he: "רשות ימית / רשות נמל", es: "Autoridad marítima / puerto", it: "Autorità marittima / porto", fr: "Autorité maritime / port", de: "Seebehörde / Hafenbehörde", pt: "Autoridade marítima / porto", ar: "السلطة البحرية / الميناء", el: "Ναυτική / λιμενική αρχή", ru: "Морская / портовая власть", tr: "Deniz / liman otoritesi", zh: "海事 / 港口机构"
};

export default function RegulationDetailPage() {
  const { lang = "en", country, topic } = useParams();
  const baseDetail = regulations[`${country}-${topic}`] || regulations[country] || {
    title: `Sailing Regulations in ${deslugify(country)}`,
    description: `Public planning guide for sailing regulations in ${deslugify(country)}, including entry preparation, documentation, safety obligations, and skipper planning context.`,
    highlights: ["Country entry planning", "Documentation reminders", "Port and marina procedure context", "Safety preparation", "Local rule awareness", "Official-source verification"],
    faqs: [
      { question: "Is this official legal advice?", answer: "No. This is planning content. Always confirm with official maritime, customs, marina, and port authorities." },
      { question: "Is regulation content public?", answer: "Yes. Regulation discovery content is public and indexable." }
    ]
  };
  const detail = getLocalizedRegulationDetail({ lang, detail: baseDetail, country, topic });
  const labels = metaLabels[lang] || metaLabels.en;
  const reviewedDate = "2026-06-27";
  const effectiveDate = detailStartDates[topic] || "2026-01-01";
  const responsible = authorityLabels[lang] || authorityLabels.en;
  const changed = "Added freshness metadata, authority ownership, and clearer rule context.";

  return (
    <SeoDetailLayout
      {...detail}
      backTo={`/${lang}/regulations`}
      backLabel={backLabels[lang] || backLabels.en}
      schemaType="Article"
      freshness={{
        labels,
        reviewed: reviewedDate,
        effective: effectiveDate,
        responsible,
        changed
      }}
      longformContent={<RegulationLongformGuide lang={lang} title={detail.title} country={country} topic={topic} />}
    />
  );
}